import React, { useRef, useState } from 'react';
import type { FieldValues } from 'react-hook-form';

import ButtonWithIcon from '../button/ButtonWithIcon';
import { IconClose16, IconClose24 } from '../icons/ic-close';
import { IconSearch16, IconSearch24 } from '../icons/ic-search';
import BasicInput, { BasicInputProps } from '../input/BasicInput';

export type SearchBarProps<TFieldName extends FieldValues = FieldValues> = {
    id?: string;
    className?: string;
    basicInputProps?: BasicInputProps<TFieldName>;
    label?: string;
    placeholder?: string;
    value?: string;
    leadingAction?: () => void;
    leadingActionIcon?: string | React.JSX.Element | StaticImageData;
    onChange?: (value: string) => void;
    onFocus?: (state: boolean) => void;
    onBlur?: () => void;
    onInputClick?: () => void;
    searchInputRef?: React.RefObject<HTMLInputElement>;
    customIcon?: React.JSX.Element;
    trailingIconSize?: '16' | '24';
    trailingIconColor?: string;
    showInput?: boolean;
    /**
     * Wait this long after the last keystroke before `onChange` fires. Callers that debounce the value
     * themselves pass 0, so the two delays do not stack and leave a stale value for a moment.
     */
    debounceMs?: number;
    /** Accessible name of the ✕ button, shown while the field has text. */
    clearLabel?: string;
    /** Accessible name of the 🔍 button, shown while the field is empty. */
    searchLabel?: string;
};

function SearchBar<TFieldName extends FieldValues>(
    props: SearchBarProps<TFieldName>,
    ref: React.LegacyRef<HTMLDivElement> | undefined
): React.JSX.Element {
    const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [currentValue, setCurrentValue] = useState(props.value ?? undefined);
    const hasText = !!currentValue;
    const debounceMs = props.debounceMs ?? 300;

    const fallbackSearchInputRef = useRef<HTMLInputElement>(null);
    const searchInputRef = props.searchInputRef ?? fallbackSearchInputRef;

    // A parent that changes `value` (a reset to '') wins over what was typed; adjusted during render, not in an effect.
    const [syncedValue, setSyncedValue] = useState(props.value);
    if (props.value !== syncedValue) {
        setSyncedValue(props.value);
        setCurrentValue(props.value ?? undefined);
    }

    function cancelPendingChange() {
        if (searchTimeout.current !== null) {
            clearTimeout(searchTimeout.current);
            searchTimeout.current = null;
        }
    }

    function onSearchInput(input: string) {
        setCurrentValue(input);
        cancelPendingChange();

        if (debounceMs <= 0) {
            props.onChange?.(input);
            return;
        }
        searchTimeout.current = setTimeout(() => {
            searchTimeout.current = null;
            props.onChange?.(input);
        }, debounceMs);
    }

    function onLeadingIconClick() {
        if (props.leadingAction) {
            props.leadingAction();
        }
    }

    function onTrailingIconClick() {
        if (searchInputRef && searchInputRef.current) {
            searchInputRef.current.value = '';
        }
        setCurrentValue(undefined);
        cancelPendingChange();
        props.onChange?.('');
    }

    function trailingIcon(): React.JSX.Element {
        const iconSearchClassName = `transform duration-150 ${hasText ? 'rotate-180 opacity-0' : ''} ${
            props.trailingIconColor
        }`;
        const iconCloseClassName = `absolute top-0 transform duration-150 ${hasText ? 'rotate-180' : 'opacity-0'} ${
            props.trailingIconColor
        }`;

        const size16: React.JSX.Element = (
            <div className="relative">
                <IconSearch16 className={iconSearchClassName} />
                <IconClose16 className={iconCloseClassName} />
            </div>
        );

        const size24: React.JSX.Element = (
            <div className="relative">
                <IconSearch24 className={iconSearchClassName} />
                <IconClose24 className={iconCloseClassName} />
            </div>
        );

        switch (props.trailingIconSize) {
            case '16':
                return size16;
            case '24':
                return size24;
            default:
                return size16;
        }
    }
    return (
        <div
            id={props.id}
            className={`${props.className} flex flex-row space-x-8`}
            ref={ref}
            onClick={props.onInputClick}>
            {props.leadingAction && (
                <ButtonWithIcon
                    icon={props.leadingActionIcon ?? ''}
                    onClick={onLeadingIconClick}
                    type="button"
                />
            )}
            <BasicInput
                {...props.basicInputProps}
                className="hide-search-input-clear-button"
                value={currentValue}
                label={props.label}
                title={props.placeholder}
                placeholder={props.placeholder}
                trailingIcon={props.customIcon ? props.customIcon : trailingIcon()}
                trailingIconAction={onTrailingIconClick}
                trailingIconLabel={hasText ? props.clearLabel : props.searchLabel}
                onChange={onSearchInput}
                onFocusChange={(state) => props.onFocus && props.onFocus(state)}
                type="search"
                inputRef={searchInputRef}
                onBlur={props.onBlur}
            />
        </div>
    );
}

export default React.forwardRef(SearchBar);
